import React from 'react';
import { Loader, RefreshCw } from 'lucide-react';

const S = ({ className = '', style = {} }) => (
  <div className={`skeleton ${className}`} style={style} />
);

const CompanyCardSkeleton = () => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3">
    <div className="flex items-start gap-3">
      <S className="w-14 h-14 rounded-xl flex-shrink-0" />
      <div className="flex-1 flex flex-col gap-2">
        <S className="h-4 w-3/4" />
        <S className="h-3 w-1/2" />
        <S className="h-3 w-1/3" />
      </div>
    </div>
    <div className="flex gap-2 flex-wrap">
      <S className="h-5 w-16 rounded-full" />
      <S className="h-5 w-20 rounded-full" />
      <S className="h-5 w-14 rounded-full" />
    </div>
    <S className="h-9 w-full rounded-lg mt-1" />
  </div>
);

/**
 * DashboardSkeleton — deux phases de chargement progressif :
 *   • profileReady=false  → squelette complet (header anonyme + banner spinner)
 *   • profileReady=true   → vrai header utilisateur + banner spinner "Profile loaded…"
 */
const DashboardSkeleton = ({
  profileReady = false,
  userName     = '',
  userInitial  = '?',
  userPhoto    = null,
  eventName    = '',
  subEventName = '',
  onReload,
}) => (
  <div className="min-h-screen bg-gray-50">

    {/* ── Header ── */}
    <div className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex justify-between items-center gap-2">

          {/* Gauche */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {profileReady ? (
              <div className="min-w-0">
                <h1 className="text-sm sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                  {eventName || 'Matchmaking'}
                </h1>
                {subEventName && (
                  <p className="text-xs text-gray-500 truncate hidden sm:block">{subEventName}</p>
                )}
              </div>
            ) : (
              <>
                <S className="h-10 w-28 rounded-lg" />
                <div className="hidden sm:flex flex-col gap-1.5">
                  <S className="h-4 w-40" />
                  <S className="h-3 w-28" />
                </div>
              </>
            )}
          </div>

          {/* Droite */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">

            {/* Bouton reload */}
            {onReload && (
              <button
                onClick={onReload}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                title="Recharger"
              >
                <RefreshCw size={15} />
              </button>
            )}

            {profileReady ? (
              <>
                {userName && (
                  <div className="hidden sm:block text-right">
                    <p className="text-xs text-gray-400">Connecté en tant que</p>
                    <p className="text-sm font-semibold text-gray-900 leading-tight">{userName}</p>
                  </div>
                )}
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex-shrink-0 overflow-hidden ring-2 ring-blue-100">
                  {userPhoto ? (
                    <img src={userPhoto} alt={userName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                      {userInitial}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <S className="h-4 w-20 hidden md:block rounded" />
                <div className="hidden sm:flex flex-col gap-1.5 items-end">
                  <S className="h-3 w-24" />
                  <S className="h-4 w-32" />
                </div>
                <S className="w-10 h-10 rounded-full" />
                <S className="w-10 h-8 rounded-lg hidden sm:block" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* ── Tabs ── */}
    <div className="bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex gap-1 py-2">
          {[80, 90, 88, 96, 72, 64].map((w, i) => (
            <S key={i} className="h-8 rounded-md flex-shrink-0" style={{ width: w }} />
          ))}
        </div>
      </div>
    </div>

    {/* ── Content ── */}
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">

      {/* Banner chargement */}
      <div className="flex items-center gap-3 bg-white rounded-xl border border-blue-100 shadow-sm px-4 py-3.5 mb-6">
        <Loader size={20} className="animate-spin text-blue-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-700">Loading your information…</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {profileReady
              ? 'Profile loaded · Fetching your matches and meetings'
              : 'Connecting to your event profile'}
          </p>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="flex gap-2 mb-4">
        <S className="h-10 flex-1 rounded-xl" />
        <S className="h-10 w-24 rounded-xl" />
      </div>
      {/* Tags */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[56, 72, 64, 80, 60].map((w, i) => (
          <S key={i} className="h-7 rounded-full" style={{ width: w }} />
        ))}
      </div>
      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <CompanyCardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

export default DashboardSkeleton;
